import { config } from '../config';
import { CDNFile, CDNUpload, TranslationVersion } from '../types';
import { approvalHeaders, readAdminError, type Approval } from './approvalRequests';
import { getAdminApiAuthHeaders } from './auth';

export const cdnApi = {
  /**
   * List all files in CDN bucket via API
   */
  async listFiles(prefix: string = ''): Promise<CDNFile[]> {
    const headers = await getAdminApiAuthHeaders();
    const url = new URL(`${config.api.url}/admin/cdn/files`);
    if (prefix) {
      url.searchParams.set('prefix', prefix);
    }

    const response = await fetch(url.toString(), { headers });
    if (!response.ok) {
      throw new Error('Failed to list files');
    }

    const data = await response.json();
    return data.items || [];
  },

  /**
   * Get content version info from CDN
   */
  async getVersionInfo(): Promise<TranslationVersion | null> {
    try {
      const response = await fetch(`${config.cdn.url}/content/version.json`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch version info:', error);
      return null;
    }
  },

  /**
   * Upload file to CDN using presigned URL
   */
  async uploadFile(upload: CDNUpload): Promise<CDNFile> {
    const headers = await getAdminApiAuthHeaders();

    // Get presigned URL from API
    const response = await fetch(`${config.api.url}/admin/cdn/upload-url`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        key: upload.path,
        contentType: upload.contentType || upload.file.type,
        cacheControl: upload.cacheControl || 'public, max-age=31536000',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to get upload URL');
    }

    const data = await response.json();
    const uploadUrl = data.upload_url || data.uploadUrl;
    const cdnUrl = data.cdn_url || data.cdnUrl;

    // Upload directly to S3 using presigned URL
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: upload.file,
      headers: {
        'Content-Type': upload.contentType || upload.file.type,
        'Cache-Control': upload.cacheControl || 'public, max-age=31536000',
      },
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file');
    }

    return {
      key: upload.path,
      size: upload.file.size,
      lastModified: new Date().toISOString(),
      url: cdnUrl,
      type: this.getFileType(upload.path),
    };
  },

  /**
   * Delete file from CDN via API.
   *
   * DM-6437: deleteCdnFile is an FM-105 destructive operation, so the call
   * carries the reason + approval headers a second operator's approval yields.
   */
  async deleteFile(key: string, approval: Approval): Promise<void> {
    const headers = await getAdminApiAuthHeaders();

    const response = await fetch(`${config.api.url}/admin/cdn/files`, {
      method: 'DELETE',
      headers: { ...headers, ...approvalHeaders(approval) },
      body: JSON.stringify({ key }),
    });

    if (!response.ok && response.status !== 204) {
      throw await readAdminError(response, 'Failed to delete file');
    }
  },

  /**
   * Determine file type from key
   */
  getFileType(key: string): CDNFile['type'] {
    if (key.startsWith('content/') && key.endsWith('.json')) {
      return 'CONTENT';
    }
    if (key.includes('config') || key.includes('experiments')) {
      return 'CONFIG';
    }
    return 'ASSET';
  },

  /**
   * Get file categories
   */
  async getFilesByCategory() {
    const files = await this.listFiles();

    return {
      content: files.filter(f => f.type === 'CONTENT'),
      assets: files.filter(f => f.type === 'ASSET'),
      configs: files.filter(f => f.type === 'CONFIG'),
    };
  },

  /**
   * Get CDN statistics
   */
  async getStats() {
    const files = await this.listFiles();
    const totalSize = files.reduce((sum, f) => sum + f.size, 0);

    const versionInfo = await this.getVersionInfo();

    return {
      totalFiles: files.length,
      totalSize,
      contentFiles: files.filter(f => f.type === 'CONTENT').length,
      assetFiles: files.filter(f => f.type === 'ASSET').length,
      currentVersion: versionInfo?.version || 'Unknown',
      lastPublished: versionInfo?.timestamp || 'Unknown',
    };
  },
};
